import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AppService } from '../../services/app.service';
import { PersonService } from '../../services/person.service';
import { ICanDeactivateGuard } from '../../core/CanDeactivateGuard';
import { ClassifierService } from '../../services/classifier.service';
import { IPersonSearchResultItem } from '../../models/Person';
import { EmployeeApplicationService } from '../../services/employee-application.service';
import { IEmployee } from '../../models/EmployeeApplication';
import { MessageService } from '../../services/message.service';
import { Observable, forkJoin } from 'rxjs';

/**
 * Employee application form page
 */
@Component({
    selector: 'app-employee-application-form',
    styleUrls: ['./application.component.scss'],
    templateUrl: './form.component.html'
})
export class EmployeeApplicationFormComponent implements OnInit, ICanDeactivateGuard {
    constructor(
        private app: AppService,
        private service: EmployeeApplicationService,
        private personService: PersonService,
        private classifierService: ClassifierService,
        private messageService: MessageService,
        private route: ActivatedRoute
    ) { }

    isDenied: boolean;
    isReady: boolean;
    title: string;
    description: string;
    type: string;
    isPersonPickerOpened: boolean;
    canPickEmployee: boolean;
    employeeData: IEmployee[];
    selectedEmployee: IEmployee;
    finished: boolean;

    readonly employeeOptionDisplayFn = (option: IEmployee) => `${option.StructureName} (${option.WorkloadName})`;

    private canDeactivateForm: () => Observable<boolean>;

    ngOnInit() {
        const user = this.app.currentUser;

        if (user) {
            this.canPickEmployee = user && user.rights.indexOf('EMPLOYEE_APPLICATION.SET_EMPLOYEE') > -1;

            const para = this.route.snapshot.queryParams;
            const upn = para['upn'];

            if (upn && !this.canPickEmployee) {
                this.isDenied = true;
                return;
            }

            const reqType = para['type'];

            if (!reqType) {
                this.isDenied = true;
                return;
            }

            const messageBeforeCode = `EmployeeApplication${reqType}Before`;

            this.app.addLoading(forkJoin([
                this.classifierService.getByCode(`EmployeeApplicationType${reqType}`, true),
                this.messageService.getText([messageBeforeCode], this.app.currentLanguage, true)
            ])).subscribe(([typeValue, messages]) => {

                if (!typeValue) {
                    this.isDenied = true;
                    this.app.showError(this.app.translate('employeeApplication_typeUnknown'));
                    return;
                }

                this.title = typeValue?.Value;
                this.description = messages.find(t => t.Code == messageBeforeCode)?.Text;
                this.type = reqType;

                if (this.canPickEmployee) {
                    if (upn) {
                        this.app.addLoading(this.personService.findEmployees('Upn', upn)).subscribe(data => {
                            this.isReady = true;

                            if (data.length) {
                                this.pickPerson(data[0]);
                            } else {
                                this.app.showError(this.app.translate('employeeNotFound'));
                                this.togglePersonPicker();
                            }
                        });
                    } else {
                        this.isReady = true;
                        this.togglePersonPicker();
                    }
                } else {
                    this.loadEmployeeData();
                    this.isReady = true;
                }
            });
        }
    }

    canDeactivate() {
        return this.canDeactivateForm ? this.canDeactivateForm() : true;
    }

    togglePersonPicker() {
        this.isPersonPickerOpened = !this.isPersonPickerOpened;

        if (this.isPersonPickerOpened) {
            this.selectedEmployee = undefined;
        }
    }

    pickPerson(person: IPersonSearchResultItem) {
        this.isPersonPickerOpened = false;
        this.finished = false;

        this.loadEmployeeData(person.Upn, person);
    }

    onFormInit(event: { canDeactivate?: () => Observable<boolean> }) {
        this.canDeactivateForm = event.canDeactivate;
    }

    onFormDone() {
        this.finished = true;
    }

    private loadEmployeeData(upn?: string, personData?: IPersonSearchResultItem) {
        this.app.addLoading(this.service.getEmployee(upn)).subscribe(data => {
            // TODO employee data should already contain this information
            if (personData) {
                data.forEach(t => {
                    if (personData) {
                        t.Email = personData.Email;
                        t.FirstName = personData.FirstName;
                        t.LastName = personData.LastName;
                    } else {
                        t.Email = this.app.currentUser.email;
                        t.FirstName = this.app.currentUser.firstName;
                        t.LastName = this.app.currentUser.lastName;
                    }
                });
            }

            this.employeeData = data;
            this.selectedEmployee = data.length && data[0];
            this.isDenied = false;
        }, err => {
            this.selectedEmployee = null;
            this.isDenied = true;
        });
    }
}
